// Grievances: clause 31 of the v6 driver agreement and Addendum No. 1.
// db/migrations/0720 to 0730, docs/GRIEVANCES.md.
//
// Four views of one register. Open grievances by stage and by who holds them,
// the overdue list, the register itself with filters, and the board summary
// (31.13, no names). Opening a row shows its full timeline and the next step.
//
// EVERY ROW IS FULL OR RESTRICTED, and Postgres decides which. A restricted row
// says a matter exists, its reference, its stage, who holds it and when it is
// due. It never says who raised it or what it says, so the person a grievance
// is about can see that there is one and nothing else.
//
// Nothing on this page decides, closes or moves a grievance on its own. The
// clock in Postgres writes to people and flags tasks; people decide.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Link2, Lock, MessageSquarePlus, RefreshCw, ShieldAlert, Users2 } from 'lucide-react'
import { useGetGrievanceOptions, useGetGrievances, useGetGrievancesAccess, useMakeGrievanceFormLink } from '../hooks/backend/grievances'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Chip } from '../components/interviews/Chip'
import { LogGrievanceDialog } from '../components/grievances/LogGrievanceDialog'
import { GrievanceDetail } from '../components/grievances/GrievanceDetail'
import {
  CHANNEL_LABEL, STAGE_HELP, STATUS_LABEL, byHolder, formatDay, groupByStage,
  type ListResult, type Options, type Row, type Summary,
} from '../utils/grievances'

type Tab = 'open' | 'overdue' | 'register' | 'board'

export default function Grievances() {
  const accessQuery = useGetGrievancesAccess<{ allowed: boolean }>()
  const listQuery = useGetGrievances<ListResult>()
  const optionsQuery = useGetGrievanceOptions<Options>()
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>((params.get('tab') as Tab) || 'open')
  const [openId, setOpenId] = useState<string | null>(params.get('g'))
  const [logging, setLogging] = useState(false)
  const [linking, setLinking] = useState(false)
  const [filter, setFilter] = useState({ status: '', stage: '', category: '', channel: '', year: '', month: '', q: '', include_archived: false })

  const loadAccess = accessQuery.trigger
  const loadList = listQuery.trigger
  const loadOptions = optionsQuery.trigger
  useEffect(() => { void loadAccess({}, { skipCache: true }).catch(() => undefined) }, [loadAccess])
  const allowed = accessQuery.data?.allowed === true

  const refresh = useCallback(() => {
    const f: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(filter)) if (v !== '' && v !== false) f[k] = v
    void loadList(f, { skipCache: true }).catch(() => undefined)
  }, [loadList, filter])
  useEffect(() => { if (allowed) refresh() }, [allowed, refresh])
  useEffect(() => { if (allowed) void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [allowed, loadOptions])

  const openGrievance = (id: string | null) => {
    setOpenId(id)
    const next = new URLSearchParams(params)
    if (id) next.set('g', id); else next.delete('g')
    setParams(next, { replace: true })
  }

  const data = listQuery.data ?? null
  const open = data?.open ?? []
  const overdue = useMemo(() => open.filter((r) => r.overdue || r.meeting_overdue), [open])

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Checking access…</div>
  }
  if (accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{accessQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]"><ShieldAlert className="h-6 w-6" /></div>
          <p className="text-sm font-medium text-foreground">Grievances are open to Driver Servants, managers, the Operational Servant and the Chief Servant.</p>
          <p className="text-xs text-muted-foreground">If you should be one of them, ask an administrator to give you a servant role.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]"><Users2 className="h-5 w-5" /></div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Grievances</h1>
          <p className="text-xs text-muted-foreground">Clause 31. Every grievance is acknowledged in writing, answered in writing, and never held against the man who raised it.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refresh()} disabled={listQuery.loading}>
          {listQuery.loading ? <Spinner className="mr-1 h-4 w-4" /> : <RefreshCw className="mr-1 h-4 w-4" />} Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => setLinking(true)} disabled={!optionsQuery.data}><Link2 className="mr-1 h-4 w-4" /> A driver's form link</Button>
        <Button variant="brand" size="sm" onClick={() => setLogging(true)} disabled={!optionsQuery.data}><MessageSquarePlus className="mr-1 h-4 w-4" /> Log a grievance</Button>
      </div>

      {data && <RoleStrip data={data} />}

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        {([
          ['open', `Open (${data?.counts.open ?? 0})`],
          ['overdue', `Overdue (${overdue.length})`],
          ['register', 'Register'],
          ['board', 'Board summary'],
        ] as [Tab, string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === k ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]' : 'text-muted-foreground hover:bg-accent'}`}>
            {label}
          </button>
        ))}
        {data && data.counts.mine > 0 && <Chip tone="brand" className="ml-auto">{data.counts.mine} held by you</Chip>}
      </div>

      {listQuery.error && <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{listQuery.error}</div>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!data ? (
          <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the register…</p>
        ) : tab === 'open' ? (
          <OpenView rows={open} onOpen={openGrievance} />
        ) : tab === 'overdue' ? (
          overdue.length === 0 ? <Empty text="Nothing is overdue. Every open grievance is inside its deadline." /> : <RowList rows={overdue} onOpen={openGrievance} />
        ) : tab === 'register' ? (
          <RegisterView rows={data.register} filter={filter} setFilter={setFilter} options={optionsQuery.data ?? null} onOpen={openGrievance} />
        ) : (
          <BoardView data={data} />
        )}
      </div>

      <LogGrievanceDialog open={logging} onOpenChange={setLogging} options={optionsQuery.data ?? null} onSaved={() => refresh()} />
      <FormLinkDialog open={linking} onClose={() => setLinking(false)} options={optionsQuery.data ?? null} />
      <GrievanceDetail id={openId} onClose={() => openGrievance(null)} onChanged={() => refresh()} options={optionsQuery.data ?? null} openRows={open} />
    </div>
  )
}

function RoleStrip({ data }: { data: ListResult }) {
  const r = data.roles
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 border-b border-border bg-muted/30 px-4 py-2 text-[11px] text-muted-foreground">
      <span>Operational Servant: <b className="text-foreground">{r.operational_servant ?? 'nobody, so her part goes to the Chief Servant'}</b></span>
      <span>Chief Servant: <b className="text-foreground">{r.chief_servant ?? 'nobody'}</b></span>
      <span>Board chair: <b className="text-foreground">{r.board_chair ?? 'not set'}</b></span>
      <span>Grievances by email: <b className="text-foreground">{r.mailbox}</b>{r.mail_enabled ? '' : ' (not read yet: the mailbox is not switched on)'}</span>
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{text}</div>
}

function OpenView({ rows, onOpen }: { rows: Row[]; onOpen: (id: string) => void }) {
  if (rows.length === 0) return <Empty text="No open grievances." />
  const holders = byHolder(rows)
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap gap-2">
        {holders.map((h) => (
          <Chip key={h.holder} tone={h.overdue ? 'red' : 'neutral'} icon={h.overdue ? <AlertTriangle className="h-3 w-3" /> : undefined}>
            {h.holder}: {h.open} open{h.overdue ? `, ${h.overdue} overdue` : ''}
          </Chip>
        ))}
      </div>
      {groupByStage(rows).map((g) => (
        <section key={g.stage} className="rounded-lg border border-border">
          <header className="border-b border-border px-3 py-2">
            <h2 className="text-sm font-semibold text-foreground">{g.label} <span className="font-normal text-muted-foreground">({g.rows.length})</span></h2>
            <p className="text-[11px] text-muted-foreground">{STAGE_HELP[g.stage]}</p>
          </header>
          <RowList rows={g.rows} onOpen={onOpen} />
        </section>
      ))}
    </div>
  )
}

function RowList({ rows, onOpen }: { rows: Row[]; onOpen: (id: string) => void }) {
  return (
    <ul className="divide-y divide-border">
      {rows.map((r) => (
        <li key={r.id}>
          <button type="button" onClick={() => onOpen(r.id)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left hover:bg-accent/40">
            <span className="w-28 shrink-0 font-mono text-xs text-muted-foreground">{r.ref}</span>
            <span className="min-w-[10rem] flex-1">
              {r.full ? (
                <>
                  <span className="block text-sm font-medium text-foreground">{r.employee ?? 'Name not given'}</span>
                  <span className="block text-xs text-muted-foreground">{r.category_label}{r.channel ? `, ${CHANNEL_LABEL[r.channel] ?? r.channel}` : ''}</span>
                </>
              ) : (
                <span className="flex items-center gap-1 text-sm text-muted-foreground"><Lock className="h-3.5 w-3.5" /> {r.confidential ? 'A confidential matter' : 'A grievance you are not part of'}</span>
              )}
            </span>
            <span className="text-xs text-muted-foreground">{r.holder ? `${r.stage_label}, ${r.holder}` : r.stage_label}</span>
            <span className="w-32 text-right text-xs">
              {r.status === 'open'
                ? <span className={r.overdue ? 'font-semibold text-rose-700 dark:text-rose-300' : 'text-foreground'}>{r.overdue ? 'Overdue since ' : 'Answer by '}{formatDay(r.answer_by)}</span>
                : <span className="text-muted-foreground">{STATUS_LABEL[r.status]}</span>}
              {r.meeting_overdue && !r.overdue && <span className="block text-amber-700 dark:text-amber-300">meeting overdue</span>}
            </span>
            <span className="flex gap-1">
              {r.urgent && <Chip tone="red">Urgent</Chip>}
              {r.late && <Chip tone="amber">Late</Chip>}
              {r.collective && <Chip tone="neutral">Collective</Chip>}
              {r.ccma_referred_on && <Chip tone="neutral">CCMA</Chip>}
              {r.is_test && <Chip tone="neutral">Test</Chip>}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

function RegisterView({ rows, filter, setFilter, options, onOpen }: {
  rows: Row[]; filter: Record<string, string | boolean>; setFilter: (f: never) => void; options: Options | null; onOpen: (id: string) => void
}) {
  const set = (k: string, v: string | boolean) => (setFilter as unknown as (f: Record<string, string | boolean>) => void)({ ...filter, [k]: v })
  const year = new Date().getFullYear()
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <Input className="h-8 w-48 text-xs" placeholder="Reference or name" value={String(filter.q)} onChange={(e) => set('q', e.target.value)} aria-label="Search" />
        <NativeSelect className="h-8 w-40 text-xs" value={String(filter.status)} onChange={(e) => set('status', e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-36 text-xs" value={String(filter.stage)} onChange={(e) => set('stage', e.target.value)} aria-label="Stage">
          <option value="">Any stage</option>
          {['step_one', 'step_two', 'chief_servant', 'board', 'urgent', 'harassment'].map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-44 text-xs" value={String(filter.category)} onChange={(e) => set('category', e.target.value)} aria-label="Category">
          <option value="">Any category</option>
          {(options?.categories ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-40 text-xs" value={String(filter.channel)} onChange={(e) => set('channel', e.target.value)} aria-label="Channel">
          <option value="">Any channel</option>
          {Object.entries(CHANNEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-24 text-xs" value={String(filter.year)} onChange={(e) => set('year', e.target.value)} aria-label="Year">
          <option value="">Any year</option>
          {[year, year - 1, year - 2, year - 3].map((y) => <option key={y} value={y}>{y}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-28 text-xs" value={String(filter.month)} onChange={(e) => set('month', e.target.value)} aria-label="Month">
          <option value="">Any month</option>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{new Date(2026, i, 1).toLocaleString('en-ZA', { month: 'long' })}</option>)}
        </NativeSelect>
        <label className="flex items-center gap-1 text-xs text-muted-foreground"><input type="checkbox" checked={filter.include_archived === true} onChange={(e) => set('include_archived', e.target.checked)} /> Archived (over 3 years)</label>
      </div>
      <p className="px-4 pt-2 text-[11px] text-muted-foreground">Category and channel filters match only the grievances you may read in full. Confidential matters stay in the list by reference.</p>
      {rows.length === 0 ? <Empty text="No grievance matches these filters." /> : <RowList rows={rows} onOpen={onOpen} />}
    </div>
  )
}

function SummaryCard({ title, s }: { title: string; s: Summary }) {
  return (
    <section className="rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mb-3 text-[11px] text-muted-foreground">{formatDay(s.from)} to {formatDay(s.to)}. Counts only, no names (31.13).</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
        <dt className="text-muted-foreground">Raised</dt><dd className="font-semibold tabular-nums">{s.raised}</dd>
        <dt className="text-muted-foreground">Resolved</dt><dd className="font-semibold tabular-nums">{s.resolved}</dd>
        <dt className="text-muted-foreground">Median days to resolve</dt><dd className="font-semibold tabular-nums">{s.median_days_to_resolve ?? '—'}</dd>
        <dt className="text-muted-foreground">Overdue at any point</dt><dd className="font-semibold tabular-nums">{s.overdue}</dd>
        <dt className="text-muted-foreground">Escalations</dt><dd className="font-semibold tabular-nums">{s.escalations}</dd>
        <dt className="text-muted-foreground">CCMA referrals</dt><dd className="font-semibold tabular-nums">{s.ccma_referrals}</dd>
        <dt className="text-muted-foreground">Confidential</dt><dd className="font-semibold tabular-nums">{s.confidential}</dd>
        <dt className="text-muted-foreground">Open now (overdue)</dt><dd className="font-semibold tabular-nums">{s.open_now} ({s.overdue_now})</dd>
      </dl>
      <div className="mt-3">
        <p className="text-xs font-medium text-foreground">By category</p>
        {s.by_category.length === 0 ? <p className="text-xs text-muted-foreground">None raised in this period.</p> : (
          <ul className="mt-1 space-y-1 text-xs">
            {s.by_category.map((c) => (
              <li key={c.category} className="flex items-center gap-2">
                <span className="w-56">{c.label}</span>
                <span className="h-2 rounded bg-[hsl(var(--brand))]" style={{ width: `${Math.max(4, (c.count / Math.max(1, s.raised)) * 200)}px` }} />
                <span className="tabular-nums">{c.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function BoardView({ data }: { data: ListResult }) {
  const quarters = data.reports.filter((r) => r.kind === 'quarterly_board')
  const months = data.reports.filter((r) => r.kind === 'monthly_review')
  return (
    <div className="space-y-4 p-4">
      <p className="text-xs text-muted-foreground">
        The Chief Servant reviews the register every month and reports to the board every quarter. On the first day of each month and each quarter a task is raised for {data.roles.chief_servant ?? 'the Chief Servant'} with the summary on it. The first review is of October 2026, the first board summary of the fourth quarter of 2026.
      </p>
      <SummaryCard title="This quarter so far" s={data.this_quarter} />
      {quarters.map((q) => <SummaryCard key={q.from} title={`Board summary, ${formatDay(q.from)} to ${formatDay(q.to)}`} s={q.summary} />)}
      {months.length > 0 && (
        <section className="rounded-lg border border-border p-4 text-xs">
          <h3 className="mb-2 text-sm font-semibold text-foreground">Monthly reviews</h3>
          <ul className="space-y-1">{months.map((m) => <li key={m.from}>{formatDay(m.from)}: {m.summary.raised} raised, {m.summary.resolved} resolved, {m.summary.overdue} overdue, {m.summary.escalations} escalated.</li>)}</ul>
        </section>
      )}
    </div>
  )
}

function FormLinkDialog({ open, onClose, options }: { open: boolean; onClose: () => void; options: Options | null }) {
  const make = useMakeGrievanceFormLink<{ url: string; name: string }>()
  const [person, setPerson] = useState('')
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { if (open) { setPerson(''); setError(null) } }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-card p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-foreground">A driver's own link to the grievance form</h2>
        <p className="text-xs text-muted-foreground">Send it to him on WhatsApp. It opens the form already knowing who he is. The form also works without a link: he gives his name and number.</p>
        <NativeSelect value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Driver">
          <option value="">Choose the driver</option>
          {(options?.employees ?? []).map((p) => <option key={p.person_id} value={p.person_id}>{p.name}</option>)}
        </NativeSelect>
        {error && <p className="text-xs text-rose-700">{error}</p>}
        {make.data?.url && person && make.data.name === (options?.employees ?? []).find((p) => p.person_id === person)?.name && (
          <div className="space-y-1">
            <Input readOnly value={make.data.url} onFocus={(e) => e.currentTarget.select()} aria-label="Link" />
            <p className="text-[11px] text-muted-foreground">Only for {make.data.name}. Anyone with this link can raise a grievance in his name, so send it to him alone.</p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
          <Button size="sm" variant="brand" disabled={!person || make.loading}
            onClick={() => { setError(null); void make.trigger({ person_id: person }).catch((e) => setError(e instanceof Error ? e.message : String(e))) }}>
            Make the link
          </Button>
        </div>
      </div>
    </div>
  )
}
